import {FC, useEffect, useState} from 'react';
import {BODY1, defaultPage, defaultPageSize} from '@/utils/constants';
import {Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography} from '@mui/material';
import ActionsMenu from '@/shared/components/Table/ActionMenu';
import Pagination from '@/shared/components/Pagination';
import {ReactComponent as ArrowUpDownIcon} from '@icons/arrow-up-down.svg';
import {ReactComponent as DeactivateIcon} from '@icons/shield-cross.svg';
import {ReactComponent as RestoreIcon} from '@icons/shield-tick.svg';
import organization from '@images/organization-example.png';
import Modal from '@/shared/components/Modal';
import {getErrorMessage} from '@/utils/helpers';
import {toast} from 'react-toastify';
import {getAllOrganizationsEvents, getEventsListSelector} from '@/store/admin';
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import SearchHeader from '@/shared/components/Table/SearchHeader';
import {IEventStatus, IAdminEvent} from '@/utils/types/superAdmin.interface';
import EmptyTableData from '@/shared/components/Table/EmptyTableData';
import Loader from '@/shared/components/Loader';
import dayjs from 'dayjs';
import {eventsTableStyle} from './Events.style';
import {EventStatus} from '@/utils/enums';
import {SuperAdminService} from '@/utils/services';

export interface IFilterGroup {
  title: string;
  options: IFilterOption[];
}

export interface IFilterOption {
  title: string;
  isSelected: boolean;
  isCheckBox?: boolean;
  id?: number | null;
  value?: string | null;
}

export const defaultFilterOptions: IFilterGroup[] = [
  {
    title: 'Status',
    options: [
      {title: 'All events', isSelected: true, value: 'AllEvents'},
      {title: 'Active', isSelected: false, value: 'active'},
      {title: 'Blocked', isSelected: false, value: 'blocked'},
    ],
  },
];
const columnOptions: string[] = ['id', 'name', 'organization', 'date', 'status'];

const Events: FC = () => {
  const dispatch = useAppDispatch();

  const baseURL = process.env.REACT_APP_FE_URL;

  const [searchName, setSearchName] = useState('');
  const [page, setPage] = useState<number>(defaultPage);
  const [filterOptions, setFilterOptions] = useState<IFilterGroup[]>(defaultFilterOptions);
  const [isChangeStatusModalOpen, setIsChangeStatusModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<IAdminEvent | null>(null);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [sortColumn, setSortColumn] = useState<string>('');
  const [isOrderDesc, setIsOrderDesc] = useState(false);

  const {currentPage, pageSize, totalCount, data} = useAppSelector(getEventsListSelector) ?? {
    currentPage: 1,
    totalPages: 0,
    pageSize: defaultPageSize,
    totalCount: 0,
    data: [],
  };

  const handleFilterChange = (groupTitle: string, optionValue: string) => {
    setPage(1);

    const updatedOptions = filterOptions.map((group) => {
      if (group.title !== groupTitle) return group;

      return {
        ...group,
        options: group.options.map((opt) => ({
          ...opt,
          isSelected: opt.value === optionValue,
        })),
      };
    });

    setFilterOptions(updatedOptions);
  };

  const handleSearchChange = (name: string) => {
    setSearchName(name);
    setPage(1);
  };

  const getStatusSx = (status: string) => {
    if (status.includes('Active')) return eventsTableStyle.statusActive;
    if (status.includes('Blocked')) return eventsTableStyle.statusBlocked;
    return eventsTableStyle.statusDefault;
  };

  const handlePressEnter = () => {
    setPage(defaultPage);
  };

  const handleSortChange = (newSortColumn: string) => () => {
    setSortColumn(newSortColumn);
    const isAsc = sortColumn !== newSortColumn || !isOrderDesc;

    setIsOrderDesc(isAsc);
    setPage(1);
  };

  const handleRestoreEvent = async () => {
    try {
      await SuperAdminService.updateEventStatus({
        id: selectedEvent?.id || 0,
        status: EventStatus.ACTIVE as IEventStatus,
      });
      await fetchEvents();
      setIsChangeStatusModalOpen(false);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };
  const handleBlockEvent = async () => {
    try {
      await SuperAdminService.updateEventStatus({
        id: selectedEvent?.id || 0,
        status: EventStatus.BLOCKED as IEventStatus,
      });
      await fetchEvents();
      setIsChangeStatusModalOpen(false);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const fetchEvents = async () => {
    setIsDataLoaded(false);
    const data = {
      page,
      pageSize: defaultPageSize,
      ...(searchName && {searchName}),
      isOrderDesc,
      sortColumn: sortColumn ?? '',
      status: filterOptions[0].options.find((opt) => opt.isSelected)?.value as IEventStatus,
    };
    try {
      await dispatch(getAllOrganizationsEvents(data)).unwrap();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
    setIsDataLoaded(true);
  };

  useEffect(() => {
    fetchEvents();
  }, [page, searchName, filterOptions, isOrderDesc, sortColumn]);

  return (
    <Box sx={eventsTableStyle.wrapper}>
      <SearchHeader
        page={page}
        items={totalCount < defaultPageSize ? totalCount : defaultPageSize}
        total={totalCount}
        onPressEnter={handlePressEnter}
        onSearchChange={handleSearchChange}
        onFilterChange={handleFilterChange}
        filterOption={filterOptions}
      />
      {isDataLoaded ? (
        totalCount ? (
          <Box sx={eventsTableStyle.tableContent}>
            <TableContainer sx={eventsTableStyle.container}>
              <Table sx={eventsTableStyle.table} aria-label="simple table">
                <TableHead>
                  <TableRow sx={eventsTableStyle.tableHeader}>
                    {columnOptions.map((col) => {
                      const sortable = col === 'id' || col === 'name' || col === 'date';

                      return (
                        <TableCell key={col}>
                          <Box
                            sx={sortable ? eventsTableStyle.headerCellTitleSortable : eventsTableStyle.headerCellTitle}
                          >
                            {col[0].toUpperCase() + col.slice(1)}
                            {sortable && (
                              <Box sx={eventsTableStyle.sortIcon} onClick={handleSortChange(col)}>
                                <ArrowUpDownIcon />
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                      );
                    })}
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody sx={eventsTableStyle.tableBody}>
                  {data.map((row) => (
                    <TableRow key={row.id}>
                      {columnOptions.map((col) => {
                        if (col === 'organization') {
                          return (
                            <TableCell key={col}>
                              <Box sx={eventsTableStyle.organizationCell}>
                                <Box sx={eventsTableStyle.avatar}>
                                  <img src={row.organizationLogo || organization} alt="avatar" />
                                </Box>
                                <Box>
                                  <Typography sx={eventsTableStyle.name}>{row.organizationName}</Typography>
                                  <Typography sx={eventsTableStyle.email}>{row.organizationEmail}</Typography>
                                </Box>
                              </Box>
                            </TableCell>
                          );
                        }
                        if (col === 'date') {
                          return (
                            <TableCell key={col}>
                              <Box>
                                <Typography>
                                  <span style={eventsTableStyle.dateLabel}>Start: </span>
                                  {`${dayjs.utc(row.dateStart).format('DD.MM.YYYY')} ${dayjs
                                    .utc(row.dateStart)
                                    .format('HH:mm')}`}
                                </Typography>
                                <Typography>
                                  <span style={eventsTableStyle.dateLabel}>End: </span>
                                  {`${dayjs.utc(row.dateEnd).format('DD.MM.YYYY')} ${dayjs
                                    .utc(row.dateEnd)
                                    .format('HH:mm')}`}
                                </Typography>
                              </Box>
                            </TableCell>
                          );
                        }
                        if (col === 'id') {
                          return (
                            <TableCell key={col} sx={eventsTableStyle.textID}>
                              {row.id}
                            </TableCell>
                          );
                        }
                        if (col === 'name') {
                          return (
                            <TableCell key={col} sx={eventsTableStyle.name}>
                              {row.name}
                            </TableCell>
                          );
                        }
                        if (col === 'status') {
                          return (
                            <TableCell key={col}>
                              <Typography sx={getStatusSx(row.status)}>{row.status}</Typography>
                            </TableCell>
                          );
                        }
                        return null;
                      })}
                      <TableCell sx={eventsTableStyle.menu}>
                        <ActionsMenu
                          content={
                            <Box>
                              <Typography
                                sx={eventsTableStyle.menuItem}
                                onClick={() => {
                                  window.open(`${baseURL}/${row.customizeWebpage}`, '_blank', 'noopener,noreferrer');
                                }}
                                variant={BODY1}
                              >
                                Open event page
                              </Typography>
                              <Typography
                                sx={eventsTableStyle.menuItem}
                                onClick={() => {
                                  setSelectedEvent(row);
                                  setIsChangeStatusModalOpen(true);
                                }}
                                variant={BODY1}
                              >
                                {row.status === 'Active' ? 'Block event' : 'Restore event'}
                              </Typography>
                            </Box>
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={eventsTableStyle.paginationContainer}>
              <Pagination
                page={currentPage}
                pageSize={pageSize}
                total={totalCount}
                onChange={(step) => setPage(step)}
              />
            </Box>
          </Box>
        ) : (
          <EmptyTableData text="No Events Found" />
        )
      ) : (
        <Loader parentClassName={eventsTableStyle.loader} size={50} />
      )}
      <Modal
        mainButtonTitle={selectedEvent?.status === 'Active' ? 'Block Event' : 'Restore Event'}
        closeButtonTitle="Cancel"
        open={isChangeStatusModalOpen}
        onClose={() => setIsChangeStatusModalOpen(false)}
        onSave={selectedEvent?.status === 'Active' ? handleBlockEvent : handleRestoreEvent}
        mainIcon={selectedEvent?.status === 'Active' ? <DeactivateIcon /> : <RestoreIcon />}
        sx={eventsTableStyle.modal}
        title={selectedEvent?.status === 'Active' ? 'Block Event' : 'Restore Event'}
        color={selectedEvent?.status === 'Active' ? 'error' : 'primary'}
        isShowCloseButton
        isShowSaveButton
      >
        {selectedEvent?.status === 'Active' ? (
          <Box sx={eventsTableStyle.textWrapper}>
            <Typography sx={eventsTableStyle.bold}>{selectedEvent.name} </Typography>
            <Typography sx={eventsTableStyle.description}>will be blocked. </Typography>
            <Typography sx={eventsTableStyle.bold}>{selectedEvent.name} </Typography>
            <Typography sx={eventsTableStyle.description}>will be notified that the event is blocked</Typography>
            <Typography sx={eventsTableStyle.descriptionBottom}>Are you sure you want to block this event?</Typography>
          </Box>
        ) : (
          <Box sx={eventsTableStyle.textWrapper}>
            <Typography sx={eventsTableStyle.bold}>{selectedEvent?.name} </Typography>
            <Typography sx={eventsTableStyle.description}>will be restored. </Typography>
            <Typography sx={eventsTableStyle.bold}>{selectedEvent?.name} </Typography>
            <Typography sx={eventsTableStyle.description}>will be notified that the event is restored</Typography>
            <Typography sx={eventsTableStyle.descriptionBottom}>
              Are you sure you want to restore this event?
            </Typography>
          </Box>
        )}
      </Modal>
    </Box>
  );
};

export default Events;
